import React from 'react';
import { TeilnahmeRahmen } from '../../sifa/public/TeilnahmeRahmen';
import { EINGABE, FEHLERKASTEN, HINWEISKASTEN, KNOPF_SEKUNDAER } from '../../sifa/public/teilnahmeSeiteStil';
import { MAX_NAME, TEXTE, ansichtFuer, melderAnzeige, type MeldenAnsicht } from '../../../services/material/materialMeldenLogic';
import { useMaterialMelden } from './useMaterialMelden';

/**
 * Öffentliche Meldeseite `<origin>/m/<token>` — der QR auf der
 * Nachbestellkarte (docs/materialverwaltung-plan.md, Ablauf 4.–6.).
 *
 * Wird OHNE Login gerendert (index.tsx routet am Pfad vorbei an `<App/>`).
 * Dünner Container: Zustand in useMaterialMelden, alle Texte und
 * Entscheidungen in materialMeldenLogic (`ansichtFuer`). Für das Handy gebaut:
 * ein großer Knopf, Text nie unter 16 px.
 */

const KASTEN: Record<MeldenAnsicht['ton'], string> = {
    neutral: 'text-slate-700 space-y-1',
    erfolg: 'p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 space-y-1',
    warnung: 'p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 space-y-1',
    fehler: `${FEHLERKASTEN} space-y-1`,
};

const TITEL_FARBE: Record<MeldenAnsicht['ton'], string> = {
    neutral: 'text-slate-900',
    erfolg: 'text-emerald-700',
    warnung: 'text-slate-900',
    fehler: 'text-slate-900',
};

const Haken: React.FC = () => (
    <span
        aria-hidden="true"
        className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 shrink-0"
    >
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" />
        </svg>
    </span>
);

/** Zwei Pfeile im Kreis — „Karte umdrehen“ bzw. „umgedreht lassen“. */
const Umdrehen: React.FC = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="w-8 h-8 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
        <path d="M3 21v-5h5" />
    </svg>
);

export const MaterialMeldenSeite: React.FC<{ token: string | null }> = ({ token }) => {
    const m = useMaterialMelden(token);
    const a = ansichtFuer({ schritt: m.schritt, karte: m.karte, jetzt: m.jetzt, fehlerText: m.fehlerText });
    const karte = a.kopf ? m.karte : null;
    const TitelTag = karte ? 'h2' : 'h1';

    return (
        <TeilnahmeRahmen>
            {karte && (
                <header className="space-y-1.5 pb-4 border-b border-slate-200">
                    {karte.firma && <p className="text-sm font-medium text-slate-500">{karte.firma}</p>}
                    <h1 className="text-3xl font-bold leading-tight text-slate-900 break-words">{karte.artikel.name}</h1>
                    {karte.artikel.lagerort && (
                        <p className="text-base text-slate-700 break-words">
                            <span className="font-semibold">{TEXTE.lagerort}:</span> {karte.artikel.lagerort}
                        </p>
                    )}
                    {karte.artikel.hinweis && (
                        <p className="text-base text-slate-600 break-words whitespace-pre-line">{karte.artikel.hinweis}</p>
                    )}
                </header>
            )}

            {a.titel && (
                <div className="flex items-center gap-3">
                    {a.ton === 'erfolg' && <Haken />}
                    <TitelTag className={`text-2xl font-bold ${TITEL_FARBE[a.ton]}`}>{a.titel}</TitelTag>
                </div>
            )}

            {a.zeilen.length > 0 && (
                <div className={KASTEN[a.ton]} role={a.ton === 'fehler' ? 'alert' : undefined} aria-live="polite">
                    {a.zeilen.map((z, i) => (
                        <p key={i} className={i === 0 && a.ton === 'warnung' ? 'font-semibold' : undefined}>
                            {z}
                        </p>
                    ))}
                </div>
            )}

            {a.aufforderung && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-900 text-white" role="status">
                    <Umdrehen />
                    <p className="text-xl font-bold">{a.aufforderung}</p>
                </div>
            )}

            {a.melder &&
                (m.sitzung ? (
                    <p className="text-base text-slate-600">{melderAnzeige(m.sitzung.name)}</p>
                ) : (
                    <div className="space-y-1.5">
                        <label htmlFor="material-melder-name" className="block text-base font-semibold text-slate-700">
                            {TEXTE.nameLabel}
                        </label>
                        <input
                            id="material-melder-name"
                            type="text"
                            autoComplete="name"
                            maxLength={MAX_NAME}
                            value={m.name}
                            onChange={(e) => m.setName(e.target.value)}
                            disabled={!!a.knopf?.sendet}
                            placeholder={TEXTE.namePlatzhalter}
                            className={EINGABE}
                        />
                    </div>
                ))}

            {a.knopf && (
                <button
                    type="button"
                    onClick={() => void m.bestaetigen()}
                    disabled={a.knopf.sendet}
                    aria-busy={a.knopf.sendet}
                    className="w-full min-h-[112px] px-6 py-5 rounded-2xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm transition-colors flex flex-col items-center justify-center gap-1 touch-manipulation select-none disabled:opacity-60 disabled:cursor-wait"
                >
                    <span className="text-3xl font-bold">{a.knopf.text}</span>
                    <span className="text-base font-medium text-blue-100">{a.knopf.unterzeile}</span>
                </button>
            )}

            {a.hinweis && <div className={HINWEISKASTEN}>{a.hinweis}</div>}

            {a.erneutVersuchen && (
                <button type="button" onClick={m.erneutVersuchen} className={KNOPF_SEKUNDAER}>
                    {TEXTE.erneutVersuchen}
                </button>
            )}
        </TeilnahmeRahmen>
    );
};

export default MaterialMeldenSeite;
